"use client"

import { useEffect, useState } from "react"
import { useMediaQuery } from "@/hooks/use-mobile"

const STORAGE_KEY = "goonfolio-grid-columns"

function responsiveDefault(isMobile: boolean, isTablet: boolean) {
  if (isMobile) return 2
  if (isTablet) return 3
  return 4
}

// Hard ceiling per breakpoint — cards below this column count on a given
// screen width get too small to read (thumbnail, title, footer all cramped).
// This is enforced live, not just as a one-time default, so a value picked
// on desktop (e.g. 6) doesn't stay stuck and break the layout on mobile.
function maxForBreakpoint(isMobile: boolean, isTablet: boolean) {
  if (isMobile) return 3
  if (isTablet) return 5
  return 8
}

// Deliberately separate from useResponsiveGridColumns (lib/settings-store.ts
// gridColumns) — that setting is shared with the main booru browser, so
// adjusting the grid size on a Goonfolio/folio-detail page was silently
// changing the main browsing grid everywhere else too. This hook keeps its
// own persisted value, scoped only to Goonfolio views.
export function useGoonfolioGridColumns() {
  const isMobile = useMediaQuery("(max-width: 640px)")
  const isTablet = useMediaQuery("(max-width: 1024px) and (min-width: 641px)")
  const maxColumns = maxForBreakpoint(isMobile, isTablet)

  const [rawValue, setRawValue] = useState(() => responsiveDefault(isMobile, isTablet))
  const [userSet, setUserSet] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      setRawValue(Number(stored))
      setUserSet(true)
    }
  }, [])

  // Until the user manually picks a value, track screen size changes.
  useEffect(() => {
    if (userSet) return
    setRawValue(responsiveDefault(isMobile, isTablet))
  }, [isMobile, isTablet, userSet])

  const setGridColumns = (value: number) => {
    setUserSet(true)
    setRawValue(value)
    localStorage.setItem(STORAGE_KEY, String(value))
  }

  // Always clamp what's actually rendered to the current breakpoint's ceiling —
  // this is what keeps a desktop-picked column count from breaking mobile,
  // while still remembering the user's higher preference for when they're back
  // on a wider screen.
  const gridColumns = Math.min(rawValue, maxColumns)

  return { gridColumns, setGridColumns, maxColumns }
}
